import React, {FC, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {buildInteractionIdProps} from '../../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {ClickEventHandler} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  AnalyticsDataPointTypesEnum,
  AnalyticsMetricError,
  AnalyticsNumericDataPointType,
  AnalyticsScalarMetric,
  AnalyticsScalarMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {makeGetAnalyticsMetric} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {getMetricRenderer, getVariationConfig} from '../../../helpers/analyticsFormatHelpers';
import {getMetricLabels, maybeRenderFeaturePillAndTooltip} from '../../../helpers/metricLabels';
import {renderPercentageValue} from '../../../helpers/metricRenderers/percentMetricRenderer';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {ReportGridItem} from '../../reportViewer/reportGrid';
import {computeMetricErrorTitle} from '../charts/common/metricErrorState';
import {ReportElementProps} from '../reportElement';
import {AnalyticsTooltip} from '../tooltips/analyticsTooltips';

/*
 * Props.
 */

interface AnalyticsCardProps extends ReportElementProps<AnalyticsScalarMetricsEnum> {
  width?: number;
  // For attaching a Pendo element.
  interactionId?: string;
  isOnClickDisabled?: boolean;
  onClickCustom?: ClickEventHandler;
  reportSectionId: ReportElementIdsEnum;
}

/*
 * Styles.
 */

const StyledChromelessButton = styled(ChromelessButton)<{$isDisabled?: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 31px 0;
  width: 100%;

  border: 1px solid ${(p) => p.theme.alphas.gray10};
  ${(p) => boxShadow(p.theme, `0px 1px 9px ${p.theme.alphas.gray10}`)};
  border-radius: 16px;

  background-color: ${(p) => p.theme.foreground};
  transition: background-color 0.1s;

  ${(p) =>
    !p.$isDisabled &&
    css`
      &:hover {
        background-color: ${p.theme.greys.shade10};
      }
    `}
`;

const StyledAnalyticsCardMetric = styled.div`
  width: 100%;
  ${flexCenter}
  justify-content: center;
  gap: 4px;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  line-height: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
`;

const StyledAnalyticsCardValue = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  margin: 8px 0;
  color: ${(p) => p.theme.greys.shade80};
  font-size: 24px;
  font-weight: ${fontWeights.semibold};
`;

const StyledAnalyticsCardVariation = styled.div<{$color?: string}>`
  width: 100%;
  ${flexCenter}
  justify-content: center;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: ${fontSizes.large};

  color: ${(p) => p.$color};
`;

const StyledAnalyticsCardMetricError = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  height: 17px;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

const StyledIcon = styled(Icon)`
  margin-right: 3px;
`;

const StyledSeparator = styled.span`
  margin: 0 4px;
`;

/*
 * Component.
 */

export const AnalyticsHeroCard: FC<AnalyticsCardProps> = ({
  isLoading,
  metric,
  width,
  interactionId,
  isOnClickDisabled,
  onClickCustom,
  reportSectionId,
}) => {
  const dispatch = useAppDispatch();
  const theme = useUserTheme();
  const [metricData, metricError] = useAppSelector(makeGetAnalyticsMetric(metric));

  const {label: metricLabel, tooltip, isBeta, isNew} = useMemo(() => getMetricLabels(metric), [metric]);

  if (metricError) {
    return renderErrorState(metricError, metricLabel, width);
  }

  if (isLoading || !metricData) {
    // If we're fetching data from the BE, render the loading state.
    return renderLoadingState(width);
  }

  const interactionProps = buildInteractionIdProps(interactionId);

  return (
    <ReportGridItem $width={width || 3}>
      <AnalyticsTooltip tooltip={tooltip} delay={1000} reportSectionId={reportSectionId}>
        <div {...interactionProps}>
          <StyledChromelessButton
            $isDisabled={isOnClickDisabled}
            isDisabled={isOnClickDisabled}
            onClick={(event) =>
              onClickCustom ? onClickCustom(event) : dispatch(setMetricDetailsAction(metric))
            }
          >
            <StyledAnalyticsCardMetric>
              {metricLabel}
              {maybeRenderFeaturePillAndTooltip(isBeta, isNew)}
            </StyledAnalyticsCardMetric>
            <StyledAnalyticsCardValue>
              {renderMetric(metricData.value, metricData.type)}
            </StyledAnalyticsCardValue>
            {renderVariation(metricData, theme)}
          </StyledChromelessButton>
        </div>
      </AnalyticsTooltip>
    </ReportGridItem>
  );
};

/*
 * Helpers.
 */

const renderVariation = (metricData: AnalyticsScalarMetric, theme: UserTheme) => {
  const {value = 0, previousValue = 0} = metricData;
  const {variation, variationPct, icon, color} = getVariationConfig(value, previousValue, theme);

  return (
    <StyledAnalyticsCardVariation $color={color}>
      <StyledIcon name={icon} color={color} />
      {variationPct && (
        <>
          {renderPercentageValue({value: variationPct})}
          <StyledSeparator>•</StyledSeparator>
        </>
      )}
      {variation > 0 && '+'}
      {renderMetric(variation, metricData.type)}
    </StyledAnalyticsCardVariation>
  );
};

const renderLoadingState = (width?: number) => (
  <ReportGridItem $width={width ?? 3}>
    <StyledChromelessButton>
      <StyledAnalyticsCardMetric>
        <Skeleton width="50%" height="14px" />
      </StyledAnalyticsCardMetric>
      <StyledAnalyticsCardValue>
        <Skeleton width="20%" height="28px" />
      </StyledAnalyticsCardValue>
      <StyledAnalyticsCardVariation>
        <Skeleton width="30%" height="16px" />
      </StyledAnalyticsCardVariation>
    </StyledChromelessButton>
  </ReportGridItem>
);

const renderErrorState = (error: AnalyticsMetricError, metricLabel?: string, width?: number) => {
  const errorState = () => {
    const {title, subtitle} = computeMetricErrorTitle(error);

    return (
      <TooltipCoordinator render={() => subtitle}>
        <StyledAnalyticsCardMetric>{metricLabel}</StyledAnalyticsCardMetric>
        <StyledAnalyticsCardValue>-</StyledAnalyticsCardValue>
        <StyledAnalyticsCardMetricError>Error: {title}</StyledAnalyticsCardMetricError>
      </TooltipCoordinator>
    );
  };

  return (
    <ReportGridItem $width={width ?? 3}>
      <StyledChromelessButton>{errorState()}</StyledChromelessButton>
    </ReportGridItem>
  );
};

const renderMetric = (
  value: number,
  type: AnalyticsNumericDataPointType = AnalyticsDataPointTypesEnum.NUMERICAL,
) => {
  const render = getMetricRenderer(type);
  return render({value});
};
